import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
  useCallback,
} from "react";

interface PlaybackSettingsContextProps {
  playtimePercent: number;
  setPlaytimePercent: (value: number) => void;
  autoPlayNext: boolean;
  setAutoPlayNext: (value: boolean) => void;
  shuffleOnSessionStart: boolean;
  setShuffleOnSessionStart: (value: boolean) => void;
  minVote: number;
  setMinVote: (value: number) => void;
  minCelebrationVote: number;
  setMinCelebrationVote: (value: number) => void;
  allowGuestAdd: boolean;
  setAllowGuestAdd: (val: boolean) => void;
  syncToBackend: () => Promise<void>;
}

const PlaybackSettingsContext = createContext<PlaybackSettingsContextProps | undefined>(undefined);

const STORAGE_KEYS = {
  playtimePercent: "playtimePercent",
  autoPlayNext: "autoPlayNext",
  shuffleOnSessionStart: "shuffleOnSessionStart",
  minVote: "minVote",
  minCelebrationVote: "minCelebrationVote",
  allowGuestAdd: "allowGuestAdd",
};

const parseStoredBoolean = (rawValue: string | null, fallback: boolean): boolean => {
  if (rawValue === null) return fallback;
  if (rawValue === "true") return true;
  if (rawValue === "false") return false;
  try {
    const parsed = JSON.parse(rawValue);
    return typeof parsed === "boolean" ? parsed : fallback;
  } catch {
    return fallback;
  }
};

const parseStoredNumber = (rawValue: string | null, fallback: number): number => {
  if (rawValue === null) return fallback;
  const parsed = Number(rawValue);
  return Number.isFinite(parsed) ? parsed : fallback;
};

export const PlaybackSettingsProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [playtimePercent, setPlaytimePercent] = useState<number>(100);
  const [autoPlayNext, setAutoPlayNext] = useState<boolean>(false);
  const [shuffleOnSessionStart, setShuffleOnSessionStart] = useState<boolean>(false);
  const [minVote, setMinVote] = useState<number>(1);
  const [minCelebrationVote, setMinCelebrationVote] = useState<number>(() =>
    parseStoredNumber(localStorage.getItem(STORAGE_KEYS.minCelebrationVote), 3)
  );
  const [allowGuestAdd, setAllowGuestAdd] = useState<boolean>(true);

  useEffect(() => {
    const storedPlaytime = localStorage.getItem(STORAGE_KEYS.playtimePercent);
    const storedShuffleOnSessionStart = localStorage.getItem(STORAGE_KEYS.shuffleOnSessionStart);
    const storedMinVote = localStorage.getItem(STORAGE_KEYS.minVote);
    const storedMinCelebrationVote = localStorage.getItem(STORAGE_KEYS.minCelebrationVote);
    const storedAllowGuestAdd = localStorage.getItem(STORAGE_KEYS.allowGuestAdd);

    setPlaytimePercent(parseStoredNumber(storedPlaytime, 100));
    setAutoPlayNext(false);
    setShuffleOnSessionStart(parseStoredBoolean(storedShuffleOnSessionStart, false));
    setMinVote(parseStoredNumber(storedMinVote, 1));
    setMinCelebrationVote(parseStoredNumber(storedMinCelebrationVote, 3));
    setAllowGuestAdd(parseStoredBoolean(storedAllowGuestAdd, true));
  }, []);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.playtimePercent, playtimePercent.toString());
    localStorage.setItem(STORAGE_KEYS.autoPlayNext, JSON.stringify(false));
    localStorage.setItem(STORAGE_KEYS.shuffleOnSessionStart, JSON.stringify(shuffleOnSessionStart));
    localStorage.setItem(STORAGE_KEYS.minVote, minVote.toString());
    localStorage.setItem(STORAGE_KEYS.minCelebrationVote, minCelebrationVote.toString());
    localStorage.setItem(STORAGE_KEYS.allowGuestAdd, JSON.stringify(allowGuestAdd));
  }, [playtimePercent, autoPlayNext, shuffleOnSessionStart, minVote, minCelebrationVote, allowGuestAdd]);

  const syncToBackend = useCallback(async () => {
    const payload = {
      playtimePercent,
      autoPlayNext,
      shuffleOnSessionStart,
      minVote,
      minCelebrationVote,
      allowGuestAdd,
    };
    console.log("Syncing playback settings to backend:", payload);
    // await api.post("/api/userPreferences", payload);
  }, [playtimePercent, autoPlayNext, shuffleOnSessionStart, minVote, minCelebrationVote, allowGuestAdd]);

  return (
    <PlaybackSettingsContext.Provider
      value={{
        playtimePercent,
        setPlaytimePercent,
        autoPlayNext,
        setAutoPlayNext,
        shuffleOnSessionStart,
        setShuffleOnSessionStart,
        minVote,
        setMinVote,
        minCelebrationVote,
        setMinCelebrationVote,
        allowGuestAdd,
        setAllowGuestAdd,
        syncToBackend,
      }}
    >
      {children}
    </PlaybackSettingsContext.Provider>
  );
};

export const usePlaybackSettings = (): PlaybackSettingsContextProps => {
  const context = useContext(PlaybackSettingsContext);
  if (!context) {
    throw new Error("usePlaybackSettings must be used within a PlaybackSettingsProvider");
  }
  return context;
};
